/**
 * WithoutReceiptPanel — Tab „Ohne Beleg" (architecture-bank-reconciliation.md
 * §8.6): unmatched Belastungen OHNE Beleg, exkl. erkannter Umbuchungen und
 * Rücklastschriften (Aufrufer-Vorfilter, §9.2). Je Zeile Alter-Badge
 * („seit n Tagen ohne Beleg") und Schnellaktionen:
 *   - „Beleg hochladen" (Deep-Link Eingangsrechnungen),
 *   - „Ausgabe anlegen" (Deep-Link Ausgaben; Vorbefüllung aus tx via Query),
 *   - „Kein Beleg nötig" (ignore + Grund-Enum).
 *
 * @module WithoutReceiptPanel
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useAuth } from '../../context/AuthContext';
import { centsToEuros } from '../../utils/moneyUtils';
import { ignoreTransaction } from '../../api/bankApi';
import { BankTransaction, BankTxIgnoreReason } from '../../types/bankTypes';
import logger from '../../utils/logger';
import { getDisplayCounterparty } from '../../utils/bankMatching';
import { loadReconcileData } from './loadReconcileData';
import { buildReconcileSuggestions } from './reconcileSuggestions';

export interface WithoutReceiptPanelProps {
  refreshToken: number;
}

/** Ignore reasons offered for a debit that legitimately needs no beleg. */
const NO_RECEIPT_REASONS: BankTxIgnoreReason[] = [
  'tax_payment',
  'payroll',
  'bank_fee',
  'private',
  'other',
];

function getTodayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Whole days between an ISO booking date and today (>= 0). */
function daysSince(iso: string): number {
  const then = new Date(`${iso}T00:00:00`).getTime();
  const now = new Date(`${getTodayISO()}T00:00:00`).getTime();
  if (!Number.isFinite(then)) return 0;
  return Math.max(0, Math.round((now - then) / 86_400_000));
}

const WithoutReceiptPanel: React.FC<WithoutReceiptPanelProps> = ({ refreshToken }) => {
  const { t, i18n } = useTranslation('bank');
  const { effectiveScopeUid } = useAuth();
  const locale = i18n.language || 'de-DE';

  const [txs, setTxs] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const formatAmount = useCallback(
    (cents: number) =>
      centsToEuros(cents).toLocaleString(locale, { style: 'currency', currency: 'EUR' }),
    [locale],
  );

  const load = useCallback(async () => {
    if (!effectiveScopeUid) return;
    setLoading(true);
    setError(null);
    setActionError(null);
    try {
      const d = await loadReconcileData(effectiveScopeUid);
      const s = buildReconcileSuggestions({
        unmatchedTxs: d.unmatchedTxs,
        allTxs: d.allTxs,
        matchedCreditTxs: d.matchedCreditTxs,
        invoices: d.invoices,
        expenses: d.expenses,
        accounts: d.accounts,
        todayISO: getTodayISO(),
      });
      setTxs(s.withoutReceipt);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.warn('WithoutReceiptPanel.load failed', msg);
      setError(msg || t('withoutReceipt.loadFailed', 'Daten konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [effectiveScopeUid, t]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const sorted = useMemo(
    () => [...txs].sort((a, b) => (a.bookingDate < b.bookingDate ? 1 : -1)),
    [txs],
  );

  const handleIgnore = (txId: string, reason: BankTxIgnoreReason) => {
    setBusy(true);
    setActionError(null);
    ignoreTransaction(txId, reason)
      .then(load)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        logger.warn('WithoutReceiptPanel ignore failed', msg);
        setActionError(msg || t('matching.actionFailed', 'Aktion fehlgeschlagen.'));
      })
      .finally(() => {
        // Reviewer 🟡2: busy auch nach ERFOLG zurücksetzen (nicht nur im
        // catch-Zweig) — sonst bleiben nach der ersten erfolgreichen
        // Ignorieren-Aktion alle Zeilen-Controls dauerhaft disabled.
        // `.finally` läuft erst nach `load()`, also kein Race mit dem Reload.
        setBusy(false);
      });
  };

  const expenseDeepLink = (tx: BankTransaction) => {
    const params = new URLSearchParams({
      supplier: tx.counterpartyName ?? '',
      grossCents: String(Math.abs(tx.amountCents)),
      date: tx.bookingDate,
    });
    return `/dashboard/ausgaben?${params.toString()}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <ClipLoader color="#6366f1" size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-4 text-[var(--tone-danger-fg)]">
        <p className="font-medium">{t('withoutReceipt.loadFailed', 'Daten konnten nicht geladen werden.')}</p>
        <p className="text-sm mt-1">{error}</p>
        <button onClick={load} className="mt-3 text-sm underline hover:text-red-200">
          {t('matching.retry', 'Erneut versuchen')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        {t('withoutReceipt.intro', 'Ausgaben ohne zugeordneten Beleg — lade einen Beleg hoch oder markiere sie als beleglos.')}
      </p>

      {actionError && (
        <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">
          {actionError}
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center text-muted">
          {t('withoutReceipt.empty', 'Keine Belastungen ohne Beleg. 🎉')}
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((tx) => {
            const age = daysSince(tx.bookingDate);
            return (
              <div
                key={tx.id}
                className="flex flex-col md:flex-row md:items-center gap-3 bg-surface border border-hairline rounded-lg p-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[var(--tone-danger-fg)] font-medium">{formatAmount(tx.amountCents)}</span>
                    <span className="text-faint text-xs">{tx.bookingDate}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-inset text-secondary border border-strong">
                      {t('withoutReceipt.ageBadge', 'seit {{days}} Tagen ohne Beleg', { days: age })}
                    </span>
                  </div>
                  <p className="text-xs text-muted truncate">{getDisplayCounterparty(tx)}</p>
                  <p className="text-[11px] text-faint truncate">{tx.purpose || '—'}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <Link
                    to="/dashboard/eingangsrechnungen"
                    className="text-xs px-2.5 py-1.5 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg whitespace-nowrap"
                  >
                    {t('withoutReceipt.uploadReceipt', 'Beleg hochladen')}
                  </Link>
                  <Link
                    to={expenseDeepLink(tx)}
                    className="text-xs px-2.5 py-1.5 bg-surface-inset hover:bg-surface-hover text-secondary rounded-lg whitespace-nowrap"
                  >
                    {t('withoutReceipt.createExpense', 'Ausgabe anlegen')}
                  </Link>
                  <select
                    aria-label={t('withoutReceipt.noReceipt', 'Kein Beleg nötig')}
                    defaultValue=""
                    disabled={busy}
                    onChange={(e) => {
                      const val = e.target.value as BankTxIgnoreReason | '';
                      if (val) handleIgnore(tx.id, val);
                      e.target.value = '';
                    }}
                    className="text-xs bg-surface-raised border border-hairline rounded px-2 py-1.5 text-muted focus:outline-none focus:border-indigo-500"
                  >
                    <option value="" disabled>
                      {t('withoutReceipt.noReceipt', 'Kein Beleg nötig')}
                    </option>
                    {NO_RECEIPT_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {t(`matching.ignoreReasons.${r}`, r)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WithoutReceiptPanel;
